import { useState, useCallback } from 'react';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useCloneReport } from '@ais-ui/websockets';
import { ParsedReportParams } from '@ais-ui/websockets';

import { FilterType, ReportType, ShapeType } from '../../../reportsTypes';
import { ReportCloneValues } from '../../utils/createReportCloneFormSchema';
import { buildReportSubmission } from '../../utils/reportSubmission';
import { getReportErrorMessage } from '../../utils/reportErrorHandler';
import { GeoJsonDictionary, ShapeDictionaryType } from '../../../utils';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useReportContext } from '../../../ReportContext';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface UseCloneSubmitOptions {
  report: ParsedReportParams | undefined;
  reportType: ReportType;
  shapeType: ShapeType;
  filterType: FilterType;
  shapes: any;
  filters: any;
  ports: any;
  instantShape: boolean;
  instantShapeData: GeoJsonDictionary;
  initialIsDynamic: boolean;
  dynamicShape: GeoJson | undefined;
  setLocalShowDialog: (show: boolean) => void;
  setShowPopover: (show: boolean) => void;
}

interface UseCloneSubmitReturn {
  onClone: (data: ReportCloneValues) => void;
  isSubmitting: boolean;
}

/**
 * Hook that handles the clone report submission logic.
 */
export function useCloneSubmit({
  report,
  reportType,
  shapeType,
  filterType,
  shapes,
  filters,
  ports,
  instantShape,
  instantShapeData,
  initialIsDynamic,
  dynamicShape,
  setLocalShowDialog,
  setShowPopover,
}: UseCloneSubmitOptions): UseCloneSubmitReturn {
  const { messages } = useLocalization();
  const { mutate } = useCloneReport();
  const { toast } = useToast();
  const { setShouldRefetch } = useReportContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onClone = useCallback(
    (data: ReportCloneValues) => {
      setIsSubmitting(true);

      const miscValues = data.miscellaneous as
        | Record<string, unknown>
        | undefined;
      const rawSelectedGroups = miscValues?.['selectedGroups'];
      type SelectedGroup = { value: string; label: string };
      const selectedGroups = Array.isArray(rawSelectedGroups)
        ? (rawSelectedGroups as SelectedGroup[])
        : [];

      // Build shape dictionary - prioritize instant shape data
      let fallbackShapeDictionary: ShapeDictionaryType = null;
      if (instantShape && Object.keys(instantShapeData).length > 0) {
        const shapeFieldName = reportType.shape?.shape?.[0]?.name || 'shape';
        const shapeGeometry = instantShapeData[shapeFieldName];
        if (shapeGeometry) {
          fallbackShapeDictionary = {
            shape: shapeGeometry,
          } as ShapeDictionaryType;
        }
      } else if (initialIsDynamic && dynamicShape) {
        fallbackShapeDictionary = {
          shape: dynamicShape,
        } as ShapeDictionaryType;
      }

      const submission = buildReportSubmission({
        reportType,
        values: data,
        shapeType,
        filterType,
        resources: { shapes, filters, ports },
        fallbackShapeDictionary,
        fallbackReportTemplateName:
          typeof report?.params?.RDN === 'string' ? report.params.RDN : 'N/A',
        fallbackReportName:
          typeof data.report_name === 'string' && data.report_name.trim() !== ''
            ? data.report_name
            : 'N/A',
        additionalMiscellaneous: {
          selectedGroups,
          email: data.email,
          mailAction: data.mailAction,
          countryCode: data.countryCode,
          phone: data.phone,
        },
        reportTemplateId:
          typeof report?.params?.RDU === 'string' ? report.params.RDU : null,
        instantShape,
      });

      if (!submission || !submission.params) {
        setIsSubmitting(false);
        toast({
          className: 'clone-report-error-toast',
          title: messages.genericToastTitleError,
          description: messages.reportsCloneFailedGenericMessage,
          variant: 'destructive',
        });
        return;
      }

      // Required for the report to work
      submission.params['RSE'] = false;

      mutate(
        { type: reportType.type, params: submission.params },
        {
          onSuccess: () => {
            toast({
              className: 'clone-report-success-toast',
              title: messages.genericToastTitleSuccess,
              description: messages.reportsCloneSuccessMessage,
            });
            // Trigger report list refresh to show the new cloned report
            setShouldRefetch(true);
            setIsSubmitting(false);
            setLocalShowDialog(false);
            setShowPopover(false);
          },
          onError: (error: Error) => {
            markErrorHandledLocally(error);
            const errorMessage = getReportErrorMessage(
              error,
              messages.reportsCloneFailedGenericMessage,
            );
            toast({
              className: 'clone-report-error-toast',
              title: messages.genericToastTitleError,
              description: errorMessage,
              variant: 'destructive',
            });
            setIsSubmitting(false);
            setLocalShowDialog(false);
            setShowPopover(false);
          },
        },
      );
    },
    [
      reportType,
      shapeType,
      filterType,
      shapes,
      filters,
      ports,
      instantShape,
      instantShapeData,
      initialIsDynamic,
      dynamicShape,
      report,
      mutate,
      toast,
      messages,
      setShouldRefetch,
      setLocalShowDialog,
      setShowPopover,
    ],
  );

  return { onClone, isSubmitting };
}
